import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, TextField, Typography, Grid, FormControl,
  InputLabel, Select, MenuItem, IconButton, Alert, CircularProgress,
  FormControlLabel, Switch, InputAdornment
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi, { MillingCostConfig, CostType, AllocationMethod, MillingRecipe } from '../../services/millingApi';

const MillingCostConfigForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id) && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<MillingRecipe[]>([]);

  const [formData, setFormData] = useState<Partial<MillingCostConfig>>({
    configCode: '',
    configName: '',
    description: '',
    costType: 'labor',
    ratePerUnit: 0,
    ratePerHour: 0,
    fixedCost: 0,
    allocationMethod: 'weight',
    allocationToByproductPercent: 0,
    priority: 1,
    isDefault: false,
    isActive: true,
    createdBy: 'admin',
  });

  useEffect(() => {
    fetchRecipes();
    if (isEdit) {
      fetchConfig();
    }
  }, [id]);

  const fetchRecipes = async () => {
    try {
      const response = await millingApi.getRecipes({});
      setRecipes(response.data || response);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const config = await millingApi.getCostConfig(id!);
      setFormData(config);
    } catch (err) {
      console.error(err);
      setError('Error loading cost configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof MillingCostConfig, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.configCode || !formData.configName) {
      setError('Config Code and Name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEdit) {
        await millingApi.updateCostConfig(id!, formData);
      } else {
        await millingApi.createCostConfig(formData);
      }
      navigate('/milling/costs');
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Error saving cost configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/milling/costs')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">{isEdit ? 'Edit Cost Configuration' : 'New Cost Configuration'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Config Code"
                  value={formData.configCode}
                  onChange={(e) => handleChange('configCode', e.target.value)}
                  disabled={isEdit}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  required
                  label="Config Name"
                  value={formData.configName}
                  onChange={(e) => handleChange('configName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Cost Type</InputLabel>
                  <Select
                    value={formData.costType}
                    label="Cost Type"
                    onChange={(e) => handleChange('costType', e.target.value as CostType)}
                  >
                    <MenuItem value="labor">Labor</MenuItem>
                    <MenuItem value="overhead">Overhead</MenuItem>
                    <MenuItem value="material">Material</MenuItem>
                    <MenuItem value="depreciation">Depreciation</MenuItem>
                    <MenuItem value="utilities">Utilities</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Rate Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Rate Settings</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rate per Unit"
                  value={formData.ratePerUnit}
                  onChange={(e) => handleChange('ratePerUnit', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  helperText="Cost applied per unit of output"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rate per Hour"
                  value={formData.ratePerHour}
                  onChange={(e) => handleChange('ratePerHour', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  helperText="Hourly rate for time-based costing"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Fixed Cost"
                  value={formData.fixedCost}
                  onChange={(e) => handleChange('fixedCost', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  helperText="Fixed amount per milling order"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Allocation Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Cost Allocation</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Allocation Method</InputLabel>
                  <Select
                    value={formData.allocationMethod}
                    label="Allocation Method"
                    onChange={(e) => handleChange('allocationMethod', e.target.value as AllocationMethod)}
                  >
                    <MenuItem value="weight">Weight</MenuItem>
                    <MenuItem value="volume">Volume</MenuItem>
                    <MenuItem value="market_value">Market Value</MenuItem>
                    <MenuItem value="nrv">Net Realizable Value (NRV)</MenuItem>
                    <MenuItem value="physical_units">Physical Units</MenuItem>
                    <MenuItem value="none">None (Manual)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="By-product Allocation %"
                  value={formData.allocationToByproductPercent}
                  onChange={(e) => handleChange('allocationToByproductPercent', parseFloat(e.target.value) || 0)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  helperText="% of cost allocated to by-products"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Priority"
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', parseInt(e.target.value) || 1)}
                  helperText="Lower number = higher priority"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Scope Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Scope & Validity</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Recipe (Optional)</InputLabel>
                  <Select
                    value={formData.recipeId || ''}
                    label="Recipe (Optional)"
                    onChange={(e) => handleChange('recipeId', e.target.value || undefined)}
                  >
                    <MenuItem value="">All Recipes (Global)</MenuItem>
                    {recipes.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.recipeCode} - {r.recipeName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Effective From"
                  value={formData.effectiveFrom || ''}
                  onChange={(e) => handleChange('effectiveFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Effective To"
                  value={formData.effectiveTo || ''}
                  onChange={(e) => handleChange('effectiveTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cost Account Code"
                  value={formData.costAccountCode || ''}
                  onChange={(e) => handleChange('costAccountCode', e.target.value)}
                  helperText="GL account for this cost type"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', height: '100%' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isDefault}
                        onChange={(e) => handleChange('isDefault', e.target.checked)}
                      />
                    }
                    label="Default Configuration"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                      />
                    }
                    label="Active"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/milling/costs')}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : (isEdit ? 'Update Config' : 'Create Config')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default MillingCostConfigForm;
